import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import AppAppBar from './views/AppAppBar';
import AppFooter from './views/AppFooter';
import Box from '@mui/material/Box';
import Button from './components/Button';
import Grid from '@mui/material/Grid';
import EssayCraft from '../../assets/essaycraft-v3.png';
import InkBowlPublishing from '../../assets/inkbowl-publishing-v2.png';
import InkLink from '../../assets/inklink-v2.png';
import VerseCanvas from '../../assets/versecanvas-v2.png';
import ProductHero from './views/ProductHero';
import Typography from './components/Typography';
import en from '../../data/en.json';
import withRoot from '../../withRoot';
import { useTransition, animated, AnimatedProps, useSpringRef } from '@react-spring/web';
// Third Party
import parse from 'html-react-parser';

const slides = [
  {
    "title": "EssayCraft AI",
    "subtitleOne": "Navigate subjective grading with precision",
    "subtitleTwo": "",
    "descriptionOne": "Adapt your writing to specific teachers’ preferences and biases",
    "descriptionTwo": "Revision suggestions tailored to each audience and context",
    "backgroundImage": `linear-gradient(rgba(255, 255, 255, 0),rgba(255, 255, 255, 0)), url(${EssayCraft})`,
    "link": "/about#essaycraft",
    "textColor": "black"
  },
  {
    "title": "InkBowl Publishing",
    "subtitleOne": "Selective digital & in-print opportunities",
    "subtitleTwo": "",
    "descriptionOne": "Flexible admissions support countless accepted work formats",
    "descriptionTwo": "Acquire credentials and exposure for your work year-round",
    "backgroundImage": `linear-gradient(rgba(255, 255, 255, 0),rgba(255, 255, 255, 0)), url(${InkBowlPublishing})`,
    "link": "/about#inkbowl-publishing",
    "textColor": "white"
  },
  {
    "title": "InkLink",
    "subtitleOne": "A global stage for student authors & artists",
    "subtitleTwo": "",
    "descriptionOne": "Elevate your professional profile on our multimedia social network",
    "descriptionTwo": "Showcase, curate a portfoilo, and connect with prospective clients",
    "backgroundImage": `linear-gradient(rgba(255, 255, 255, 0.2),rgba(255, 255, 255, 0.4)), url(${InkLink})`,
    "link": "/about#inklink",
    "textColor": "black"
  },
  {
    "title": "VerseCanvas AI",
    "subtitleOne": "Seamless AI-generated art & literature",
    "subtitleTwo": "",
    "descriptionOne": "Join the new wave where creativity meets intelligent generation",
    "descriptionTwo": "Enhance and redefine the possibilities of authentic expression",
    "backgroundImage": `linear-gradient(rgba(0, 0, 0, 0.2),rgba(0, 0, 0, 0.5)), url(${VerseCanvas})`,
    "link": "/about#versecanvas",
    "textColor": "white"
  }
]
const Main: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [index, set] = useState(0)
  const onClick = () => set(state => (state + 1) % 4)
  const transRef = useSpringRef()
  /* click to change slide vertically - along y axis */
  const transitions = useTransition(index, {
    ref: transRef,
    keys: index,
    from: { opacity: 0, transform: 'translate3d(0,100%,0)' },
    enter: { opacity: 1, transform: 'translate3d(0%,0%,0)' },
    leave: { opacity: 0, transform: 'translate3d(0,-50%,0)' },
  })

	useEffect(() => {
		transRef.start();

		const interval = setInterval(() => {
			set((prev) => (prev + 1) % slides.length);
		}, 5000); // every 5 seconds

		return () => clearInterval(interval); // cleanup
	}, [index]);

  return (
    <React.Fragment>
      <AppAppBar />
      <ProductHero />

			<Box
				sx={{
					maxWidth: { xs: "100vw", md: "50vw" }, // Mobile: 100vw, Desktop: 50vw
					textAlign: "center",
					m: "0 auto",
					padding: { xs: "10px 20px", md: "20px" }, // Horizontal padding on mobile to prevent overflow
          mt: { xs: 4, md: 8 }, // Further increased margin on mobile and desktop
          mb: { xs: 4, md: 8 }
				}}
			>
				<Typography variant="h5" sx={{ fontWeight: "bold", fontSize: { xs: "1.2rem", md: "1.5rem" } }}>
					{en.marketing.heroContent.title}
				</Typography>
				<Typography variant="body1" sx={{ mt: 2, fontSize: { xs: "1rem", md: "1.2rem" }, lineHeight: "2" }}>
          {en.marketing.heroContent.description}
				</Typography>
			</Box>

      <Box sx={{ height: { xs: '50vh', md: '65vh' }, overflow: 'hidden' }} onClick={onClick}>
        {transitions((style, i) => (
          <animated.div
            style={{
              ...style,
              position: 'absolute',
              width: '100%',
              height: isMobile ? '50%' : '65%',
              backgroundImage: slides[i].backgroundImage,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              color: 'white',
              flexDirection: 'column',
              textAlign: 'left',
              paddingLeft: isMobile ? "4vw" : "8vw",
              paddingTop: isMobile ? "16vw" : "6vw"
            }}
          >
            <Typography color={slides[i].textColor} align="left" variant="h3" sx={{ fontWeight: "bold", textTransform: "none", mb: 1, fontSize: { xs: "1.8rem", md: "3rem" } }}>
              {slides[i].title}
            </Typography>
            <Typography color={slides[i].textColor} align="left" variant="h5" sx={{ fontSize: { xs: "1.2rem", md: "2.2rem" }, lineHeight: "1.2", mb: 6 }}>
              {slides[i].subtitleOne}
            </Typography>
            <Typography color={slides[i].textColor} align="left" variant="body1" sx={{ fontSize: { xs: "1rem", md: "1.5rem" }, fontWeight: 100, lineHeight: "1", mb: 1, maxWidth: { xs: "70vw", md: "none" } }}>
              {slides[i].descriptionOne}
            </Typography>
            <Typography color={slides[i].textColor} align="left" variant="body1" sx={{ fontSize: { xs: "1rem", md: "1.5rem" }, fontWeight: 100, lineHeight: "1", mb: 3, maxWidth: { xs: "70vw", md: "none" } }}>
              {slides[i].descriptionTwo}
            </Typography>
						<Box
							sx={{
								position: 'absolute',
								bottom: 20,
								left: '50%',
								transform: 'translateX(-50%)',
								display: 'flex',
								gap: 1,
								zIndex: 10,
							}}
						>
							{slides.map((_, i) => (
								<Box
									key={i}
									onClick={(e) => {
										e.stopPropagation(); // prevent parent onClick (if needed)
										set(i);
									}}
									sx={{
										width: 12,
										height: 12,
										borderRadius: '50%',
										backgroundColor: i === index ? 'white' : 'rgba(255,255,255,0.4)',
										border: i === index ? '1px solid black' : '1px solid transparent',
										cursor: 'pointer',
										transition: 'all 0.3s ease-in-out',
									}}
								/>
							))}
						</Box>
          </animated.div>
        ))}
      </Box>

      <Box sx={{ backgroundColor: "black", color: "white", pl: { xs: "4vw", md: "8vw" }, pt: { xs: "10vw", md: "8vw" }, pb: { xs: "8vh", md: "4vh" } }}>
        <Typography variant="h4" align="left" sx={{fontWeight: "bold", textTransform: "none", color: "white", mb: 1, fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
          {en.marketing.oneCreator.title}
        </Typography>
        <Typography align="left" variant="body1" sx={{lineHeight: { xs: "1.5", md: "2" }, fontSize: { xs: "1.1rem", md: "1.5rem" }, fontWeight: 100, maxWidth: { xs: "42ch", md: "38ch" } }}>
          {parse(en.marketing.oneCreator.description)}
        </Typography>
      </Box>
      <AppFooter /> 
    </React.Fragment>
  );
};

export default withRoot(Main);

